import Box from '@mui/material/Box';

import { ColumnMenu } from './toolbar-column-menu';
import { ToolbarActions } from './toolbar-actions';
import { ToolbarControls } from './toolbar-controls';
import { ImportDialog } from './toolbar-import-dialog';
import { ImportExportMenu } from './toolbar-import-export-menu';

// ----------------------------------------------------------------------

type TableToolbarViewProps = {
  // Original props
  actionButtonLabel: string;
  actionButtonHandler?: () => void;
  actionButtonIcon?: string;
  secondaryActionButtonLabel?: string;
  secondaryActionButtonHandler?: () => void;
  secondaryActionButtonIcon?: string;
  isToolbarVisible: boolean;
  isActionButton?: Array<'import' | 'export'>;
  actionhref?: string;
  isShowCreateBtn?: boolean;
  isSearchFullWidth?: boolean;
  currentModule?: string;
  moduleName?: string;
  isCreate: boolean;
  isView: boolean;
  isImport: boolean;
  isExport: boolean;
  advanceFilterFields?: any;
  setAdvanceFilter?: any;
  isAdvanceFilter?: boolean;
  advanceFilters?: any;
  refreshPage?: () => void;
  refreshing?: boolean;
  resetPagination?: any;
  isDisableSync?: boolean;
  isSearchInput?: boolean;
  selectedfilters?: any;
  handleRemoveAllFilter?: () => void;
  dateTimeFiltre?: any;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
  columns?: any[];
  customFields?: any[];

  // Props from logic hook
  searchProps: any;
  columnProps: any;
  importExportProps: any;
  popovers: any;
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

export function TableToolbarView({
  actionButtonLabel,
  actionButtonHandler = () => {},
  actionButtonIcon,
  secondaryActionButtonLabel,
  secondaryActionButtonHandler,
  secondaryActionButtonIcon,
  isToolbarVisible = true,
  isActionButton = [],
  actionhref = '',
  isShowCreateBtn = true,
  isSearchFullWidth = false,
  currentModule = 'Unknown',
  moduleName = '',
  isCreate = false,
  isView = false,
  isImport = false,
  isExport = false,
  advanceFilterFields = [],
  setAdvanceFilter = () => {},
  isAdvanceFilter = false,
  advanceFilters,
  refreshPage = undefined,
  refreshing = false,
  resetPagination = () => {},
  isPrivacyPolicyUrls = null,
  isDisableSync = false,
  isSearchInput = true,
  selectedfilters = [],
  handleRemoveAllFilter = () => {},
  dateTimeFiltre = [],
  columns = [],
  customFields = [],
  searchProps,
  columnProps,
  importExportProps,
  popovers,
  t,
}: TableToolbarViewProps) {
  const { searchTerm, handleSearchChange, handleSearchKeyDown } = searchProps;
  const {
    visibleColumns,
    filteredColumns,
    columnSearch,
    setColumnSearch,
    lockedColumns,
    allVisible,
    handleToggleColumn,
    handleToggleAll,
    handleResetColumns,
  } = columnProps;
  const { handleImportClick, handleFileChange, handleExport } = importExportProps;
  const { menuActions, columnPopover, importModal } = popovers;

  return (
    <>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Left - Create Buttons and Privacy Links */}
        <ToolbarActions
          isShowCreateBtn={isShowCreateBtn}
          isCreate={isCreate}
          isView={isView}
          actionhref={actionhref}
          actionButtonLabel={actionButtonLabel}
          actionButtonHandler={actionButtonHandler}
          actionButtonIcon={actionButtonIcon}
          isDisableSync={isDisableSync}
          secondaryActionButtonLabel={secondaryActionButtonLabel}
          secondaryActionButtonHandler={secondaryActionButtonHandler}
          secondaryActionButtonIcon={secondaryActionButtonIcon}
          isPrivacyPolicyUrls={isPrivacyPolicyUrls}
          moduleName={moduleName}
        />

        {/* Right - Filters and Controls */}
        <ToolbarControls
          selectedfilters={selectedfilters}
          dateTimeFiltre={dateTimeFiltre}
          handleRemoveAllFilter={handleRemoveAllFilter}
          columnPopoverOnOpen={columnPopover.onOpen}
          isToolbarVisible={isToolbarVisible}
          isSearchInput={isSearchInput}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          handleSearchKeyDown={handleSearchKeyDown}
          refreshPage={refreshPage}
          refreshing={refreshing}
          isAdvanceFilter={isAdvanceFilter}
          advanceFilterFields={advanceFilterFields}
          setAdvanceFilter={setAdvanceFilter}
          advanceFilters={advanceFilters}
          resetPagination={resetPagination}
          isActionButton={isActionButton}
          isExport={isExport}
          isImport={isImport}
          menuActionsOnOpen={menuActions.onOpen}
          t={t}
          columns={columns}
          customFields={customFields}
        />
      </Box>
      {/* Import Dialog */}
      <ImportDialog
        open={importModal.value}
        onClose={() => importModal.onFalse()}
        onImport={handleFileChange}
        currentModule={currentModule}
        t={t}
      />

      {/* Import/Export Menu */}
      <ImportExportMenu
        open={menuActions.open}
        anchorEl={menuActions.anchorEl}
        onClose={menuActions.onClose}
        isImport={isImport}
        isExport={isExport}
        isActionButton={isActionButton}
        handleImportClick={handleImportClick}
        handleExport={handleExport}
        t={t}
      />

      {/* Column Visibility Menu */}
      <ColumnMenu
        open={columnPopover.open}
        anchorEl={columnPopover.anchorEl}
        onClose={columnPopover.onClose}
        columnSearch={columnSearch}
        setColumnSearch={setColumnSearch}
        filteredColumns={filteredColumns}
        visibleColumns={visibleColumns}
        lockedColumns={lockedColumns}
        allVisible={allVisible}
        handleToggleColumn={handleToggleColumn}
        handleToggleAll={handleToggleAll}
        handleResetColumns={handleResetColumns}
        t={t}
      />
    </>
  );
}
